/**
 * OnboardingPage — 3-step intro carousel for first-time users.
 *
 * Swipeable on mobile (drag horizontally), arrow-button navigated on
 * desktop. Skippable from any step. Marks the user as onboarded then
 * routes to the home dashboard.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Lightbulb, FileSignature, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { useAuthStore } from '@/stores/authStore';
import { useLocaleStore } from '@/stores/localeStore';
import { isRtl } from '@/i18n/config';
import { cn } from '@/utils/cn';

interface CarouselStep {
  icon: LucideIcon;
  title: string;
  body: string;
}

export function OnboardingPage() {
  const { t } = useTranslation('wizards');
  const navigate = useNavigate();
  const markOnboarded = useAuthStore((s) => s.markOnboarded);
  const locale = useLocaleStore((s) => s.locale);
  const rtl = isRtl(locale);

  const steps: readonly CarouselStep[] = [
    { icon: FileText,      title: t('onboarding.step1_title'), body: t('onboarding.step1_body') },
    { icon: Lightbulb,     title: t('onboarding.step2_title'), body: t('onboarding.step2_body') },
    { icon: FileSignature, title: t('onboarding.step3_title'), body: t('onboarding.step3_body') },
  ];

  const [index, setIndex] = useState(0);
  const isLast = index === steps.length - 1;
  const current = steps[index] ?? steps[0];
  if (!current) return null; // unreachable: steps is a non-empty constant
  const StepIcon = current.icon;

  const finish = () => {
    markOnboarded();
    navigate('/', { replace: true });
  };

  const next = () => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  };

  // Drag-to-swipe (mobile-friendly).
  const handleDragEnd = (_: unknown, info: { offset: { x: number } }) => {
    const threshold = 60;
    const x = info.offset.x;
    // Reverse direction in RTL so swiping "forward" feels natural.
    const dir = rtl ? -1 : 1;
    if (x * dir < -threshold && !isLast) setIndex((i) => i + 1);
    if (x * dir > threshold && index > 0) setIndex((i) => i - 1);
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      <header className="flex h-14 items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <GiOrbitLogo size="sm" />
          <span className="text-sm font-semibold tracking-tight">Growing Intelligence</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={finish}>
            {t('onboarding.skip')}
          </Button>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          <div className="overflow-hidden rounded-xl border border-border bg-surface p-8 shadow-raised sm:p-10">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={index}
                drag="x"
                dragElastic={0.15}
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                initial={{ opacity: 0, x: rtl ? -32 : 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: rtl ? 32 : -32 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="flex flex-col items-center text-center"
              >
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-spark-soft text-spark">
                  <StepIcon className="h-7 w-7" aria-hidden />
                </div>
                <h2 className="text-xl font-semibold tracking-tight">{current.title}</h2>
                <p className="mt-2 text-sm text-ink-muted">{current.body}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2" role="tablist" aria-label="Onboarding steps">
            {steps.map((_, i) => (
              <button
                type="button"
                key={i}
                role="tab"
                aria-selected={i === index}
                onClick={() => setIndex(i)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  i === index ? 'w-8 bg-spark' : 'w-2 bg-border',
                )}
              />
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Button size="lg" onClick={next}>
              {isLast ? t('onboarding.get_started') : t('onboarding.next')}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
